import { useState } from "react";
import { motion } from "framer-motion";
import { CalendarPlus, CalendarDays, Download, Check, Instagram, Headphones, ArrowRight, MessageCircle, ImageDown, Link2 } from "lucide-react";

const PODCAST_SPOTIFY = "https://open.spotify.com/show/62zpVyG79oV06aJg0QVGwD";
const PODCAST_APPLE = "https://podcasts.apple.com/podcast/id/1759373675";
const LUISA_PORTRAIT = "https://customer-assets-jai6qajn.emergentagent.net/job_zone-of-genius/artifacts/ha8r2yfd_IMG_7464.jpg";
const INSTAGRAM_URL = "https://www.instagram.com/luisa.bergholz/";
const SHARE_GRAPHIC = "/eigentlich-dabei-story.webp";
const SHARE_GRAPHIC_FULL = "https://customer-assets-jai6qajn.emergentagent.net/job_zone-of-genius/artifacts/hhgfs5ht_Eigentlich-Dabei-Story.PNG";

/* Masterclass: 30. Sept 2026, 19:00 Europe/Berlin (CEST = UTC+2) -> 17:00Z, 90 Min */
const ZOOM_LINK = "https://eu01web.zoom.us/j/66954741048";
const CAL = {
  title: 'Masterclass: Bye, Bye „Eigentlich ist doch alles gut“',
  startUTC: "20260930T170000Z",
  endUTC: "20260930T183000Z",
  details: `Live-Masterclass mit Luisa Bergholz.\nZoom-Link zum Beitreten: ${ZOOM_LINK}`,
  location: ZOOM_LINK,
};

const googleUrl = () => {
  const p = new URLSearchParams({
    action: "TEMPLATE",
    text: CAL.title,
    dates: `${CAL.startUTC}/${CAL.endUTC}`,
    details: CAL.details,
    location: CAL.location,
  });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
};

const downloadIcs = () => {
  const stamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Luisa Bergholz//Masterclass//DE",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    "UID:eigentlich-masterclass-20260930@luisabergholz.com",
    `DTSTAMP:${stamp}`,
    `DTSTART:${CAL.startUTC}`,
    `DTEND:${CAL.endUTC}`,
    `SUMMARY:${CAL.title}`,
    `DESCRIPTION:${CAL.details.replace(/\n/g, "\\n")}`,
    `LOCATION:${CAL.location}`,
    `URL:${ZOOM_LINK}`,
    "BEGIN:VALARM",
    "TRIGGER:-PT1H",
    "ACTION:DISPLAY",
    "DESCRIPTION:Masterclass beginnt in 1 Stunde",
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "masterclass-eigentlich.ics";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const AddToCalendar = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="add-to-calendar-button"
        className="group inline-flex items-center justify-center gap-3 rounded-sm bg-[#5C1D10] px-9 py-4 font-heading text-sm font-bold uppercase tracking-wider text-[#FFF2B0] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-16px_rgba(92,29,16,0.7)] sm:text-base"
      >
        <CalendarPlus className="h-5 w-5" />
        Trag's in Deinen Kalender
      </button>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute left-1/2 z-20 mt-3 w-64 -translate-x-1/2 overflow-hidden rounded-lg border border-[#EBC479] bg-[#FFFBEE] shadow-xl"
          data-testid="calendar-options"
        >
          <a
            href={googleUrl()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="calendar-google"
            className="flex items-center gap-3 border-b border-[#EBC479]/50 px-5 py-3.5 text-left text-sm font-medium text-[#5C1D10] transition-colors hover:bg-[#F6E7BE]"
          >
            <CalendarDays className="h-4 w-4 text-[#B26B3F]" /> Google Kalender
          </a>
          <button
            type="button"
            onClick={() => { downloadIcs(); setOpen(false); }}
            data-testid="calendar-ics"
            className="flex w-full items-center gap-3 px-5 py-3.5 text-left text-sm font-medium text-[#5C1D10] transition-colors hover:bg-[#F6E7BE]"
          >
            <Download className="h-4 w-4 text-[#B26B3F]" /> Apple / Outlook (.ics)
          </button>
        </motion.div>
      )}
    </div>
  );
};

const ease = [0.22, 1, 0.36, 1];
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay }}>
    {children}
  </motion.div>
);

/* ---- WhatsApp share ---- */
// Finaler Live-Link, der beim Teilen verschickt wird.
const SHARE_PAGE_URL = "https://success.luisabergholz.com/eigentlich";

const ShareSection = () => {
  // Shared links always point to the organic page so recommendations don't skew ad attribution.
  const shareUrl = SHARE_PAGE_URL;
  const shareText = `Ich gehe los für einen Job, der sich genauso gut anfühlt, wie er von außen aussieht: Bye, Bye „Eigentlich ist doch alles gut“ - die kostenfreie Masterclass von Luisa Bergholz am 30. September 2026 um 19 Uhr, live via Zoom. Kommst Du auch mit dazu? ${shareUrl}`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // Fallback for older browsers / non-secure contexts
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <section className="relative py-20 lg:py-24" data-testid="danke-share">
      <div className="mx-auto grid max-w-4xl items-center gap-10 px-6 sm:grid-cols-[0.7fr_1fr] lg:gap-14">
        <Reveal>
          <div className="mx-auto max-w-[260px]">
            <div className="overflow-hidden rounded-2xl border border-[#EBC479]/60 shadow-[0_24px_70px_-28px_rgba(92,29,16,0.45)]">
              <img src={SHARE_GRAPHIC} alt="Ich bin dabei - Bye, Bye „Eigentlich ist doch alles gut“ Masterclass Grafik" className="block w-full" data-testid="share-graphic" />
            </div>
            <a href={SHARE_GRAPHIC_FULL} target="_blank" rel="noopener noreferrer" download data-testid="share-graphic-download"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#8A4A2E] underline underline-offset-2 transition-colors hover:text-[#5C1D10]">
              <ImageDown className="h-3.5 w-3.5" /> Grafik für Deine Story herunterladen
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="text-center sm:text-left">
          <p className="font-accent text-2xl italic text-[#8A4A2E] sm:text-3xl">Kennst Du jemanden, die das auch braucht?</p>
          <h2 className="mt-3 font-heading text-3xl font-black leading-tight text-[#5C1D10] sm:text-4xl">Sag's weiter.</h2>
          <p className="mt-4 text-base leading-relaxed text-[#5C1D10]/80">
            Schick die Einladung zur Masterclass direkt an eine Freundin, deren Job sich auch richtig gut anfühlen darf.
          </p>
          <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-start">
            <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp"
              className="inline-flex items-center gap-3 rounded-sm bg-[#25D366] px-8 py-3.5 font-heading text-sm font-bold uppercase tracking-wider text-[#0B3D24] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_44px_-16px_rgba(37,211,102,0.6)]">
              <MessageCircle className="h-5 w-5" /> Via WhatsApp teilen
            </a>
            <button type="button" onClick={copyLink} data-testid="share-copy-link"
              className="inline-flex items-center gap-2.5 rounded-sm border border-[#B26B3F]/60 px-8 py-3.5 font-heading text-sm font-bold uppercase tracking-wider text-[#5C1D10] transition-colors duration-300 hover:bg-[#B26B3F]/10">
              {copied ? <Check className="h-5 w-5" /> : <Link2 className="h-5 w-5" />}
              {copied ? "Kopiert!" : "Link kopieren"}
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function EigentlichDanke() {
  return (
    <div className="bg-gradient-to-b from-[#FBEFC9] to-[#F4DFA6]" data-testid="danke-page">
      {/* Hero confirmation */}
      <section className="relative px-6 pb-20 pt-24 text-center lg:pt-28">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-[#EBC479] opacity-30 blur-[120px]" />
        </div>
        <div className="relative mx-auto max-w-2xl">
          <Reveal>
            <span className="inline-flex items-center gap-3 font-heading text-xs font-semibold uppercase tracking-[0.34em] text-[#8A4A2E]">
              <span className="h-px w-8 bg-[#B26B3F]" /> Masterclass
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="relative mx-auto mt-6 h-44 w-44 sm:h-52 sm:w-52">
              <img src={LUISA_PORTRAIT} alt="Luisa Bergholz" className="h-full w-full rounded-full border-2 border-[#EBC479] object-cover shadow-lg" data-testid="danke-portrait" />
              <span className="absolute bottom-1 right-1 flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#FBEFC9] bg-[#5C1D10] text-[#FFF2B0]">
                <Check className="h-6 w-6" />
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.14}>
            <h1 className="mt-7 font-heading text-4xl font-black leading-[1.05] text-[#5C1D10] sm:text-5xl lg:text-6xl">
              Yesss, Du hast Dich erfolgreich angemeldet!
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-[#5C1D10]/85">
              Du hast Dich erfolgreich für <span className="font-semibold">Bye, Bye „Eigentlich ist doch alles gut“</span> angemeldet.
            </p>
            <p className="mx-auto mt-4 font-heading text-xl font-semibold text-[#5C1D10] sm:text-2xl">
              Wir sehen uns live am 30. September 2026 um 19 Uhr.
            </p>
          </Reveal>
          <Reveal delay={0.28}>
            <p className="mt-8 text-base text-[#8A4A2E]">Trag Dir den Termin gleich hier in Deinen Kalender ein.</p>
            <div className="mt-5 flex justify-center">
              <AddToCalendar />
            </div>
          </Reveal>
        </div>
      </section>

      <ShareSection />

      {/* Podcast */}
      <section className="relative bg-[#3A130A] py-24 lg:py-28" data-testid="danke-podcast">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <Reveal>
            <p className="font-accent text-2xl italic text-[#FCEFC7] sm:text-3xl">Du willst nicht warten?</p>
            <p className="mt-2 font-heading text-4xl font-black text-[#EBC479] sm:text-5xl">I got you!</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-[#FBEFC9]/85 sm:text-lg">
              Im <span className="font-semibold text-[#FFF2B0]">SELBSTNEUERFINDERIN</span> Podcast warten hunderte Folgen rund um das Thema
              „einen Job finden, der sich wirklich nach Dir anfühlt“ auf Dich.
            </p>
          </Reveal>
          <Reveal delay={0.14}>
            <div className="mx-auto mt-9 aspect-video max-w-xl overflow-hidden rounded-xl border border-[#EBC479]/40 bg-black shadow-[0_24px_70px_-28px_rgba(0,0,0,0.6)]">
              <iframe
                src="https://www.youtube.com/embed/M-Rk8ueUAFo"
                title="Der SELBSTNEUERFINDERIN Podcast - Folge 126"
                className="h-full w-full"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                data-testid="podcast-embed"
              />
            </div>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a href={PODCAST_SPOTIFY} target="_blank" rel="noopener noreferrer" data-testid="podcast-spotify"
                className="inline-flex items-center gap-2 rounded-sm bg-[#FFF2B0] px-7 py-3.5 font-heading text-sm font-bold uppercase tracking-wider text-[#5C1D10] transition-transform duration-300 hover:-translate-y-0.5">
                <Headphones className="h-4 w-4" /> Auf Spotify anhören
              </a>
              <a href={PODCAST_APPLE} target="_blank" rel="noopener noreferrer" data-testid="podcast-apple"
                className="inline-flex items-center gap-2 rounded-sm border border-[#EBC479] px-7 py-3.5 font-heading text-sm font-bold uppercase tracking-wider text-[#EBC479] transition-colors duration-300 hover:bg-[#EBC479]/10">
                <Headphones className="h-4 w-4" /> Auf Apple Podcast anhören
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Instagram connect */}
      <section className="relative py-20 text-center" data-testid="danke-instagram">
        <div className="mx-auto max-w-xl px-6">
          <Reveal>
            <p className="font-accent text-2xl italic text-[#8A4A2E] sm:text-3xl">Let's connect!</p>
            <p className="mt-3 text-base text-[#5C1D10]/80">Folge mir auf Instagram</p>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="danke-instagram-link"
              className="mt-5 inline-flex items-center gap-3 rounded-sm bg-[#5C1D10] px-8 py-3.5 font-heading text-sm font-bold uppercase tracking-wider text-[#FFF2B0] transition-transform duration-300 hover:-translate-y-0.5">
              <Instagram className="h-5 w-5" /> @luisa.bergholz
              <ArrowRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2A0F08] py-14" data-testid="danke-footer">
        <div className="mx-auto max-w-5xl px-6">
          <div className="flex flex-col items-center gap-6">
            <p className="font-heading text-sm uppercase tracking-[0.3em] text-[#EBC479]">by Luisa Bergholz</p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-[#C9A96A]">
              <a href="https://luisabergholz.com/impressum" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-[#FFF2B0]">Impressum</a>
              <a href="https://luisabergholz.com/datenschutzerklaerung" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-[#FFF2B0]">Datenschutz</a>
              <a href="https://luisabergholz.com/agb" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-[#FFF2B0]">AGB</a>
              <a href="https://luisabergholz.com/widerrufsformular" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-[#FFF2B0]">Vertrag widerrufen</a>
              <a href="https://luisabergholz.com/kontakt" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-[#FFF2B0]">Kontakt</a>
            </div>
            <p className="text-xs text-[#8A6A4A]">© 2026 Luisa Bergholz. Alle Rechte vorbehalten.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
